import { useEffect, useState } from "react";
import { X, Coins, Check, ShieldCheck, Loader2 } from "lucide-react";
import { creditsApi, apiError } from "@/lib/api";
import { PAYMENTS } from "@/constants/testIds";
import copy from "@/data/payments-copy.json";

export function BuyCreditsModal({ open, onClose }) {
  const [packs, setPacks] = useState([]);
  const [currency, setCurrency] = useState("PLN");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setError("");
    creditsApi
      .packs()
      .then((d) => {
        setPacks(d.packs || []);
        setCurrency(d.currency || "PLN");
        const def = (d.packs || []).find((p) => p.badge) || (d.packs || [])[0];
        setSelected(def?.id || null);
      })
      .catch((e) => setError(apiError(e.response?.data?.detail, e.message)));
  }, [open]);

  if (!open) return null;

  const pay = async () => {
    if (!selected) return;
    setLoading(true);
    setError("");
    try {
      const res = await creditsApi.checkout({
        creditPackId: selected,
        clientRequestId: crypto.randomUUID(),
        originUrl: window.location.origin,
      });
      window.location.href = res.checkoutUrl;
    } catch (e) {
      setError(apiError(e.response?.data?.detail, e.message));
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end justify-center bg-black/60 backdrop-blur-sm sm:items-center"
      onClick={onClose}
      data-testid={PAYMENTS.buyModal}
    >
      <div
        className="max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-t-panel border border-brand-line bg-brand-surface p-6 sm:rounded-panel"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-1 flex items-center justify-between">
          <h2 className="font-display text-xl font-semibold text-brand-text">{copy.purchase.title}</h2>
          <button
            type="button"
            onClick={onClose}
            data-testid={PAYMENTS.buyClose}
            className="flex h-9 w-9 items-center justify-center rounded-field text-brand-sec hover:bg-brand-elevated hover:text-brand-text"
          >
            <X size={18} />
          </button>
        </div>
        <p className="mb-5 text-sm text-brand-muted">{copy.purchase.oneTimeNotice}</p>

        {error && (
          <div data-testid={PAYMENTS.buyError} className="mb-4 rounded-field border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-300">
            {error}
          </div>
        )}

        <div className="space-y-3">
          {packs.map((p) => {
            const active = selected === p.id;
            return (
              <button
                key={p.id}
                type="button"
                data-testid={`${PAYMENTS.pack}-${p.id}`}
                onClick={() => setSelected(p.id)}
                className={`flex w-full items-center gap-4 rounded-field border px-4 py-4 text-left transition-colors ${
                  active ? "border-brand-accent bg-brand-accent/10" : "border-brand-line bg-brand-bg hover:border-brand-accent/40"
                }`}
              >
                <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full ${active ? "bg-brand-accent text-brand-on" : "bg-brand-elevated text-brand-accent"}`}>
                  <Coins size={20} strokeWidth={1.75} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2">
                    <span className="font-display text-base font-semibold text-brand-text">{p.name}</span>
                    {p.badge && (
                      <span className="rounded-full bg-brand-accent/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-brand-accent">
                        {p.badge}
                      </span>
                    )}
                  </span>
                  <span className="mt-0.5 block text-sm text-brand-sec">
                    {p.creditsTotal} kredytów
                    {p.creditsBonus > 0 && <span className="text-brand-accent"> (+{p.creditsBonus} bonus)</span>}
                  </span>
                </span>
                <span className="shrink-0 text-right">
                  <span className="block font-display text-lg font-semibold text-brand-text">
                    {p.grossPrice} {currency}
                  </span>
                  {active && <Check size={16} className="ml-auto mt-1 text-brand-accent" />}
                </span>
              </button>
            );
          })}
        </div>

        <button
          type="button"
          data-testid={PAYMENTS.payButton}
          onClick={pay}
          disabled={!selected || loading}
          className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-button bg-brand-accent px-6 py-3.5 text-[15px] font-semibold text-brand-on transition-transform hover:-translate-y-0.5 disabled:opacity-60"
        >
          {loading ? <Loader2 size={18} className="animate-spin" /> : null}
          {loading ? "Przekierowanie…" : copy.purchase.buyCta}
        </button>
        <p className="mt-3 flex items-center justify-center gap-1.5 text-xs text-brand-muted">
          <ShieldCheck size={13} /> {copy.purchase.footer}
        </p>
      </div>
    </div>
  );
}
